import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { API } from './auth';
import { WORLD_PATH, projectPoint } from './world-map';

/**
 * VERAX: Casos de suplantación, junio a septiembre de 2026
 *
 * Los casos registrados de la empresa, mes a mes, con origen y técnica.
 * Responde las tres preguntas del comité: cuántos sitios nos copiaron, cuántos
 * se dieron de baja y desde dónde vienen. Lo que VERAX verificó sobre estos
 * mismos casos se ve en la pestaña "Verificación".
 */

interface Bucket { nombre: string; n: number }
interface Pais { iso: string; pais: string; lat?: number; lon?: number; sitios: number; resueltos: number; inferidos: number }
interface Mes {
  periodo: string; etiqueta: string; codigo: string | null; emitido: string | null; fuente: string;
  huellaArchivo: string | null; nota: string | null; reportados: number; dadosDeBaja: number; enProceso: number;
  tasaBaja: number; dominiosNuevos: number; credencialesValidas: number; credencialesNoValidas: number;
  dominiosOficiales: number; tecnicas: Bucket[]; tlds: Bucket[]; plataformas: Bucket[];
  origen: { paises: Pais[]; sinUbicar: number }; operadores: Bucket[];
  exposicionMedianaDias: number | null; exposicionMuestra: number; bajasVerificadas: number;
}
interface Sitio {
  url: string; domain: string; platform: string | null; tld: string; technique: string; phoneMasked: string | null;
  actorKey: string | null; status: string; originCountry: string | null; originConfidence: string;
  originBasis: string | null; registrar: string | null; operatorCountry: string | null; periodo: string;
  registeredAt?: string | null; registryStatus?: string | null; diasDesdeRegistro?: number | null;
}
interface Data {
  empresa: { id: string; name: string; domain: string } | null;
  meses: Mes[]; sitios: Sitio[];
  totales: {
    informes: number; reportados: number; dadosDeBaja: number; enProceso: number; tasaBaja: number;
    dominiosUnicos: number; promedioMensual: number; credencialesValidas: number;
    exposicionMedianaDias: number | null; exposicionMuestra: number; bajasVerificadas: number; registradores: Bucket[];
    tecnicas: Bucket[]; tlds: Bucket[]; plataformas: Bucket[]; origen: { paises: Pais[]; sinUbicar: number };
    persistentes: { dominio: string; periodos: string[]; ultimoEstado: string }[];
    reincidentes: { actor: string; telefono: string | null; sitios: string[]; periodos: string[] }[];
  } | null;
}

const C_BAJA = '#059669';
const C_PROCESO = '#d97706';

const Tile: React.FC<{ label: string; value: React.ReactNode; hint?: string }> = ({ label, value, hint }) => (
  <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
    <p className="text-[11px] uppercase tracking-wider text-gray-500">{label}</p>
    <p className="text-3xl font-bold mt-1 tabular-nums">{value}</p>
    {hint && <p className="text-xs text-gray-500 mt-1">{hint}</p>}
  </div>
);

const Bars: React.FC<{ title: string; items: Bucket[]; total: number; max?: number }> = ({ title, items, total, max = 6 }) => (
  <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
    <h4 className="text-sm font-semibold mb-3">{title}</h4>
    {items.length === 0 && <p className="text-xs text-gray-500">Sin datos</p>}
    <ul className="space-y-2">
      {items.slice(0, max).map(i => (
        <li key={i.nombre}>
          <div className="flex justify-between text-xs mb-1">
            <span className="text-gray-300 truncate pr-2" title={i.nombre}>{i.nombre}</span>
            <span className="text-gray-400 tabular-nums">{i.n} · {total ? Math.round(i.n / total * 100) : 0}%</span>
          </div>
          <div className="h-1.5 bg-gray-800 rounded">
            <div className="h-1.5 rounded bg-cyan-500" style={{ width: `${total ? (i.n / total) * 100 : 0}%` }} />
          </div>
        </li>
      ))}
    </ul>
  </div>
);

/** Barras apiladas por mes: dados de baja + en proceso. */
const MonthlyChart: React.FC<{ meses: Mes[]; selected: string | null; onSelect: (p: string | null) => void }> = ({ meses, selected, onSelect }) => {
  const [hover, setHover] = useState<Mes | null>(null);
  const max = Math.max(1, ...meses.map(m => m.reportados));
  const H = 180;
  return (
    <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
      <div className="flex items-start justify-between flex-wrap gap-2 mb-3">
        <div>
          <h4 className="text-sm font-semibold">Sitios fraudulentos por mes</h4>
          <p className="text-xs text-gray-500">Clic en una barra para ver ese mes en el mapa y la tabla.</p>
        </div>
        <div className="flex gap-4 text-xs text-gray-300" aria-label="Leyenda">
          <span className="flex items-center gap-1.5"><i className="inline-block w-3 h-3 rounded-sm" style={{ background: C_BAJA }} />Dados de baja</span>
          <span className="flex items-center gap-1.5"><i className="inline-block w-3 h-3 rounded-sm" style={{ background: `repeating-linear-gradient(45deg, ${C_PROCESO} 0 3px, #78350f 3px 5px)` }} />En proceso</span>
        </div>
      </div>
      <div className="relative">
        <div className="flex items-end gap-3 border-b border-gray-700" style={{ height: H }}>
          {meses.map(m => {
            const hB = (m.dadosDeBaja / max) * (H - 24);
            const hP = (m.enProceso / max) * (H - 24);
            const active = selected === m.periodo;
            return (
              <button key={m.periodo} type="button"
                onClick={() => onSelect(active ? null : m.periodo)}
                onMouseEnter={() => setHover(m)} onMouseLeave={() => setHover(null)}
                onFocus={() => setHover(m)} onBlur={() => setHover(null)}
                aria-label={`${m.etiqueta}: ${m.reportados} sitios, ${m.dadosDeBaja} dados de baja, ${m.enProceso} en proceso`}
                className={`flex-1 flex flex-col items-center justify-end h-full rounded-t outline-none ${active ? 'bg-gray-800/70' : 'hover:bg-gray-800/40'} focus-visible:ring-2 focus-visible:ring-cyan-500`}>
                <span className="text-xs text-gray-300 tabular-nums mb-1">{m.reportados}</span>
                <div className="w-3/5 max-w-[44px] flex flex-col gap-[2px]">
                  {m.enProceso > 0 && <div className="rounded-t" style={{ height: hP, background: `repeating-linear-gradient(45deg, ${C_PROCESO} 0 3px, #78350f 3px 5px)` }} />}
                  {m.dadosDeBaja > 0 && <div className={m.enProceso > 0 ? '' : 'rounded-t'} style={{ height: hB, background: C_BAJA }} />}
                </div>
              </button>
            );
          })}
        </div>
        <div className="flex gap-3 mt-1.5">
          {meses.map(m => (
            <span key={m.periodo} className={`flex-1 text-center text-[11px] ${selected === m.periodo ? 'text-cyan-400 font-semibold' : 'text-gray-500'}`}>
              {m.etiqueta.replace(/ (\d{2})(\d{2})$/, ' $2')}
            </span>
          ))}
        </div>
        {hover && (
          <div className="absolute top-0 right-0 bg-gray-950 border border-gray-700 rounded px-3 py-2 text-xs shadow-xl pointer-events-none">
            <p className="font-semibold capitalize mb-1">{hover.etiqueta}</p>
            <p className="text-gray-300">{hover.reportados} reportados · {hover.dominiosNuevos} nuevos</p>
            <p className="text-gray-300">{hover.dadosDeBaja} dados de baja ({hover.tasaBaja}%) · {hover.enProceso} en proceso</p>
          </div>
        )}
      </div>
    </div>
  );
};

const HistoryMap: React.FC<{ paises: Pais[]; sinUbicar: number; capa: 'infra' | 'operador'; operadores: Bucket[]; total: number }> =
  ({ paises, sinUbicar, capa, operadores, total }) => {
  const puntos = capa === 'infra'
    ? paises.filter(p => p.lat !== undefined)
    : operadores.map(o => o.nombre === 'CO' ? { iso: 'CO', pais: 'Colombia', lat: 4.57, lon: -74.3, sitios: o.n, resueltos: o.n, inferidos: 0 } : null).filter(Boolean) as Pais[];
  const max = Math.max(1, ...puntos.map(p => p.sitios));
  const [hover, setHover] = useState<Pais | null>(null);
  // Encuadre: América + Europa occidental, donde está toda la campaña observada.
  const vb = puntos.length && puntos.every(p => (p.lon ?? 0) < -30) ? '110 70 260 200' : '100 40 420 230';
  // Factor de escala: rótulos y trazos legibles en cualquier encuadre.
  const k = Number(vb.split(' ')[2]) / 260;
  const sinDato = capa === 'infra' ? sinUbicar : total - operadores.reduce((n, o) => n + o.n, 0);
  return (
    <div className="relative">
      <svg viewBox={vb} className="w-full h-auto bg-gray-950 rounded-lg border border-gray-800" role="img"
        aria-label={`Mapa de origen: ${puntos.map(p => `${p.pais} ${p.sitios}`).join(', ') || 'sin países ubicados'}`}>
        <path d={WORLD_PATH} fill="#1f2937" stroke="#374151" strokeWidth={0.3} />
        <g transform="" />
        {/* Destino: la marca vigilada está en Colombia */}
        {(() => { const [x, y] = projectPoint(6.25, -75.56); return (
          <g><circle cx={x} cy={y} r={2.2 * k} fill="none" stroke="#e5e7eb" strokeWidth={0.6 * k} />
          <text x={x - 5 * k} y={y + 1.5 * k} fontSize={4.5 * k} textAnchor="end" fill="#d1d5db">Medellín (marca)</text></g>); })()}
        {puntos.map(p => {
          const [x, y] = projectPoint(p.lat!, p.lon!);
          const [mx, my] = projectPoint(6.25, -75.56);
          const r = (2 + Math.sqrt(p.sitios / max) * 9) * k;
          // Arco origen → marca: muestra hacia dónde va la suplantación.
          const cx = (x + mx) / 2, cy = Math.min(y, my) - Math.abs(x - mx) * 0.18;
          return (
            <g key={p.iso} onMouseEnter={() => setHover(p)} onMouseLeave={() => setHover(null)} style={{ cursor: 'default' }}>
              {p.iso !== 'CO' && <path d={`M${x},${y} Q${cx},${cy} ${mx},${my}`} fill="none" stroke="#22d3ee" strokeOpacity={0.35} strokeWidth={0.8 * k} strokeDasharray={`${2 * k} ${1.5 * k}`} />}
              <circle cx={x} cy={y} r={r + 4 * k} fill="transparent" />
              <circle cx={x} cy={y} r={r} fill="#22d3ee" fillOpacity={0.35} stroke="#22d3ee" strokeWidth={0.8 * k} />
              <text x={x} y={y + 1.7 * k} fontSize={5 * k} textAnchor="middle" fill="#ecfeff" fontWeight={700}>{p.sitios}</text>
              <text x={x} y={y + r + 6 * k} fontSize={4.2 * k} textAnchor="middle" fill="#9ca3af">{p.iso}</text>
            </g>
          );
        })}
      </svg>
      {hover && (
        <div className="absolute top-2 left-2 bg-gray-950/95 border border-gray-700 rounded px-3 py-2 text-xs">
          <p className="font-semibold">{hover.pais}</p>
          <p className="text-gray-300">{hover.sitios} sitios{capa === 'infra' && ` · ${hover.resueltos} verificados por RDAP, ${hover.inferidos} estimados`}</p>
        </div>
      )}
      <p className="text-[11px] text-gray-500 mt-2">
        {capa === 'infra'
          ? <>País de la infraestructura: hosting cuando el sitio sigue en línea, o jurisdicción del registrador (RDAP) cuando ya fue retirado; si no responde, se estima por el dominio de país o la plataforma y se marca como estimado. {sinDato > 0 && <b className="text-gray-400">{sinDato} sitios sin ubicar todavía</b>}, se completan con <code>npm run historico:enriquecer</code> en el servidor.</>
          : <>País del operador según el número de contacto publicado en el sitio (prefijo móvil). {sinDato > 0 && `${sinDato} sitios no publicaban número.`}</>}
      </p>
    </div>
  );
};

const statusBadge = (s: string) => s === 'TAKEN_DOWN'
  ? <span className="text-[11px] px-2 py-0.5 rounded bg-emerald-950 text-emerald-300 border border-emerald-800 whitespace-nowrap">✓ Dado de baja</span>
  : <span className="text-[11px] px-2 py-0.5 rounded bg-amber-950 text-amber-300 border border-amber-800 whitespace-nowrap">◷ En proceso</span>;

export const HistoryPanel: React.FC = () => {
  const [data, setData] = useState<Data | null>(null);
  const [error, setError] = useState('');
  const [mes, setMes] = useState<string | null>(null);
  const [capa, setCapa] = useState<'infra' | 'operador'>('infra');
  const [filtro, setFiltro] = useState<'TODOS' | 'TAKEN_DOWN' | 'IN_PROCESS'>('TODOS');
  const [q, setQ] = useState('');

  useEffect(() => {
    setError('');
    axios.get(`${API}/history`, { params: { meses: 4 } })
      .then(r => { setData(r.data); setMes(null); })
      .catch(e => setError(e?.response?.data?.error || 'No se pudo cargar el histórico'));
  }, []);

  const vista = useMemo(() => {
    if (!data?.totales) return null;
    const m = mes ? data.meses.find(x => x.periodo === mes) : null;
    const sitios = data.sitios.filter(s => !mes || s.periodo === mes);
    const operadores = (() => {
      const c = new Map<string, number>();
      sitios.forEach(s => { if (s.operatorCountry) c.set(s.operatorCountry, (c.get(s.operatorCountry) ?? 0) + 1); });
      return Array.from(c.entries()).map(([nombre, n]) => ({ nombre, n }));
    })();
    return {
      titulo: m ? m.etiqueta : 'junio a septiembre',
      reportados: m ? m.reportados : data.totales.reportados,
      bajas: m ? m.dadosDeBaja : data.totales.dadosDeBaja,
      proceso: m ? m.enProceso : data.totales.enProceso,
      tasa: m ? m.tasaBaja : data.totales.tasaBaja,
      tecnicas: m ? m.tecnicas : data.totales.tecnicas,
      tlds: m ? m.tlds : data.totales.tlds,
      plataformas: m ? m.plataformas : data.totales.plataformas,
      origen: m ? m.origen : data.totales.origen,
      operadores, sitios, mesObj: m,
      exposicion: m ? m.exposicionMedianaDias : data.totales.exposicionMedianaDias,
      muestra: m ? m.exposicionMuestra : data.totales.exposicionMuestra,
      verificadas: m ? m.bajasVerificadas : data.totales.bajasVerificadas,
      registradores: (() => {
        const c = new Map<string, number>();
        sitios.forEach(s => { if (s.registrar) c.set(s.registrar, (c.get(s.registrar) ?? 0) + 1); });
        return Array.from(c.entries()).map(([nombre, n]) => ({ nombre, n })).sort((a, b) => b.n - a.n);
      })()
    };
  }, [data, mes]);

  if (error) return <p className="text-red-400">{error}</p>;
  if (!data) return <p className="text-gray-500">Cargando histórico…</p>;
  if (!data.totales || !vista) return (
    <div className="bg-gray-900 border border-gray-800 rounded-lg p-8 text-center text-gray-400">
      No hay casos registrados. El operador los carga con <code>npm run historico:importar</code>.
    </div>
  );

  const tabla = vista.sitios
    .filter(s => filtro === 'TODOS' || s.status === filtro)
    .filter(s => !q || s.domain.includes(q.toLowerCase()) || s.technique.toLowerCase().includes(q.toLowerCase()));

  return (
    <div>
      <div className="flex justify-between items-start mb-5 flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold">Casos de suplantación</h2>
          <p className="text-sm text-gray-400">
            {data.empresa?.name} · junio a septiembre de 2026 · {data.meses.length} meses
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
        <Tile label={`Sitios reportados · ${vista.titulo}`} value={vista.reportados}
          hint={!mes ? `${data.totales.promedioMensual.toLocaleString('es-CO')} por mes en promedio` : `${vista.mesObj?.dominiosNuevos} dominios nuevos`} />
        <Tile label="Dados de baja" value={<span style={{ color: '#34d399' }}>{vista.bajas}</span>} hint={`${vista.tasa}% del total · ${vista.verificadas} verificados hoy en RDAP`} />
        <Tile label="En proceso" value={<span style={{ color: '#fbbf24' }}>{vista.proceso}</span>} hint="pendientes del proveedor o la autoridad" />
        <Tile label="Exposición mediana" value={vista.exposicion !== null ? <>{vista.exposicion}<span className="text-base text-gray-400"> días</span></> : '-'}
          hint={`del registro del dominio a su notificación · n=${vista.muestra}`} />
        <Tile label="Actores reincidentes" value={data.totales.reincidentes.length}
          hint="mismo número de contacto en varios sitios" />
      </div>

      <div className="mb-5"><MonthlyChart meses={data.meses} selected={mes} onSelect={setMes} /></div>

      {vista.mesObj?.nota && (
        <div className="mb-5 text-sm bg-amber-950/40 border border-amber-800 text-amber-200 rounded p-3">{vista.mesObj.nota}</div>
      )}

      <div className="grid md:grid-cols-3 gap-4 mb-5">
        <div className="md:col-span-2 bg-gray-900 border border-gray-800 rounded-lg p-4">
          <div className="flex justify-between items-center mb-3 flex-wrap gap-2">
            <h4 className="text-sm font-semibold">Desde dónde · <span className="capitalize text-gray-400 font-normal">{vista.titulo}</span></h4>
            <div className="flex gap-1 text-xs" role="group" aria-label="Capa del mapa">
              <button onClick={() => setCapa('infra')} className={`px-2 py-1 rounded ${capa === 'infra' ? 'bg-gray-700' : 'text-gray-400'}`}>Infraestructura</button>
              <button onClick={() => setCapa('operador')} className={`px-2 py-1 rounded ${capa === 'operador' ? 'bg-gray-700' : 'text-gray-400'}`}>Operador</button>
            </div>
          </div>
          <HistoryMap paises={vista.origen.paises} sinUbicar={vista.origen.sinUbicar} capa={capa}
            operadores={vista.operadores} total={vista.sitios.length} />
        </div>
        <div className="space-y-4">
          <Bars title="Técnica de suplantación" items={vista.tecnicas} total={vista.reportados} />
          <Bars title="Extensión (TLD)" items={vista.tlds} total={vista.reportados} max={5} />
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4 mb-5">
        <div className="space-y-4">
          <Bars title="Registrador del dominio (canal de baja)" items={vista.registradores} total={vista.reportados} max={5} />
          <Bars title="Plataformas de terceros usadas" items={vista.plataformas} total={vista.reportados} max={4} />
        </div>
        <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
          <h4 className="text-sm font-semibold mb-1">Actores reincidentes</h4>
          <p className="text-xs text-gray-500 mb-3">Sitios distintos con el mismo número de contacto (enmascarado). Insumo para la denuncia ante la Fiscalía.</p>
          <ul className="space-y-2 max-h-56 overflow-auto">
            {data.totales.reincidentes.slice(0, 12).map(a => (
              <li key={a.actor} className="text-xs border-b border-gray-800 pb-2">
                <span className="font-mono text-cyan-300">{a.actor}</span> <span className="text-gray-400">{a.telefono}</span>
                <span className="text-gray-500"> · {a.sitios.length} sitios · {a.periodos.join(', ')}</span>
                <p className="text-gray-400 truncate" title={a.sitios.join(', ')}>{a.sitios.join(' · ')}</p>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
        <div className="flex justify-between items-center flex-wrap gap-2 mb-3">
          <h4 className="text-sm font-semibold">Sitios · <span className="capitalize text-gray-400 font-normal">{vista.titulo}</span> ({tabla.length})</h4>
          <div className="flex gap-2 items-center">
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Buscar dominio o técnica"
              className="bg-gray-950 border border-gray-700 rounded px-2 py-1 text-sm" />
            <select value={filtro} onChange={e => setFiltro(e.target.value as any)} className="bg-gray-950 border border-gray-700 rounded px-2 py-1 text-sm">
              <option value="TODOS">Todos</option><option value="TAKEN_DOWN">Dados de baja</option><option value="IN_PROCESS">En proceso</option>
            </select>
          </div>
        </div>
        <div className="overflow-auto max-h-[620px]">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-gray-900"><tr className="text-left text-[11px] uppercase tracking-wider text-gray-500 border-b border-gray-800">
              <th className="py-2 pr-3">Mes</th><th className="pr-3">Dominio</th><th className="pr-3">Técnica</th>
              <th className="pr-3">Origen</th><th className="pr-3">Contacto</th><th>Estado</th>
            </tr></thead>
            <tbody>
              {tabla.map((s, i) => (
                <tr key={`${s.periodo}-${i}`} className="border-b border-gray-800/60 align-top">
                  <td className="py-2 pr-3 text-gray-400 whitespace-nowrap">{s.periodo}</td>
                  <td className="pr-3"><span className="font-mono text-xs break-all">{s.domain}</span>
                    {s.platform && <span className="block text-[11px] text-gray-500">en {s.platform}</span>}</td>
                  <td className="pr-3 text-xs text-gray-300">{s.technique}</td>
                  <td className="pr-3 text-xs text-gray-400" title={s.originBasis || ''}>
                    {s.originCountry ? `${s.originCountry}${s.originConfidence === 'INFERIDO' ? ' (est.)' : ''}` : '-'}
                    {s.registrar && <span className="block text-[11px] text-gray-500">{s.registrar.replace(/ d\/b\/a.*$/, '')}</span>}
                    {s.registeredAt && <span className="block text-[11px] text-gray-500">registrado {String(s.registeredAt).slice(0, 10)}{typeof s.diasDesdeRegistro === 'number' && ` · ${s.diasDesdeRegistro} d`}</span>}</td>
                  <td className="pr-3 text-xs font-mono text-gray-400 whitespace-nowrap">{s.phoneMasked ?? '-'}{s.actorKey && <span className="block text-[10px] text-cyan-400">{s.actorKey}</span>}</td>
                  <td>{statusBadge(s.status)}
                    {s.registryStatus && /hold/i.test(s.registryStatus) && <span className="block text-[10px] text-emerald-500 mt-1">suspendido por registrador</span>}
                    {s.registryStatus === 'ELIMINADO' && <span className="block text-[10px] text-gray-500 mt-1">dominio liberado</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-[11px] text-gray-600 mt-3">
          Fuente: registro de casos del monitoreo de la empresa, cargado en VERAX como línea base. Los números de contacto se
          muestran enmascarados y las credenciales filtradas no se almacenan en la plataforma.
        </p>
      </div>
    </div>
  );
};
